import { Box, Card, CardHeader, CircularProgress, debounce, FormControlLabel, FormGroup, Drawer, Grid, Stack,
  InputAdornment, Skeleton, Switch, TextField, Typography, IconButton, Tooltip, useMediaQuery, FormHelperText, 
  CardContent, Button, Alert } from "@mui/material";
import { ListContextProvider, ReduxState, useGetMany, useGetOne, useListContext, useListController, useRecordContext } from "ra-core";
import { ChangeEventHandler, FC, useMemo, useState } from "react";
import SearchIcon from '~/icons/Search';
import EditIcon from '@mui/icons-material/Edit';
import ClientsTable from "~/components/settings/clients/ClientsTable";
import StringArrayTextField from "~/components/input/StringArrayTextField";
import { useSelector } from "react-redux";
import Scrollbar from "~/components/Scrollbar";
import { Helmet } from 'react-helmet-async';
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import { FunctionField } from "~/components/field";
import { RecPatch } from "~/dataProvider/tools";
import { ClientManageDto } from "~/gen/api/user";
import { ClientSettingDto } from "~/gen/api/securefile/api";
import CreateOrEdit from "~/controller/CreateOrEdit";
import WithSave from "~/controller/WithSave";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import { Content } from "~/components/content/Content";
import { OdResourcePageProps } from "~/types/resource";
import usePermitGuard from "~/authProvider/usePermitGuard";
import { MANAGE_SECUREFILE_SETTINGS } from "~/constants";


const clientsSecurefileRes = 'settings-clients-securefile'
const securefilePerClientSettingRes = 'settings-securefile-per-client'


const SecureFilePerClientSetting: FC<OdResourcePageProps> = ({options}) => {
  var listCtrls = useListController({ 
    resource: clientsSecurefileRes, 
    basePath: '', 
    sort: defaultSort,
  })
  usePermitGuard([[MANAGE_SECUREFILE_SETTINGS]])
  return (
    <Content pageName="SecureFile Settings - Clients" resourceName="Clients" breadcrumbs={options?.breadcrumbs}>
      <ListContextProvider value={listCtrls}>
        <SecureFilePerClientSettingBody />
      </ListContextProvider>
    </Content>
  )
}

const SecureFilePerClientSettingBody: FC = () => {
  const listCtx = useListContext()
  const { ids } = listCtx
  
  const loading = useSelector<ReduxState, boolean>(store => !!store.admin.loading)

  const [states, setStates] = useState({
    term: '',
    refetchId: null as null|string,
    toResume: null as ClientSettingDto|null,
  })
  
  const debouncedSetTerm = useMemo(() => {
    const setTerm = (s:string) => {
      const v = listCtx.filterValues
      listCtx.setFilters({...v, term: s}, {})
    }
    return debounce(setTerm, 300)
  }, [listCtx])

  const handleTermChange : ChangeEventHandler<HTMLInputElement> = (e)=>{
    debouncedSetTerm(e.target.value)
    setStates(v => ({...v, term: e.target.value}))
  }

  const perClientSettingData : (ClientSettingDto | undefined)[] = (useGetMany(securefilePerClientSettingRes, ids)?.data ?? []) as any
  const refetchClientData = useGetMany(securefilePerClientSettingRes, ids)?.refetch

  const { loading: refetchingSingle } = useGetOne(
    states.refetchId == null ? 'noop' : securefilePerClientSettingRes, 
    states.refetchId ?? -1,
    useMemo(() => {
      const clearRefetch = () => setStates(v => v.refetchId ? {...v, refetchId:null} : v)
      return { onSuccess: clearRefetch, onFailure: clearRefetch }
    }, [])
  )
  const saveHandlers = useDefaultSavedHandlers("Edit SecureFile Client Setting")

  return (
    <>
      <Card sx={{ mt: 5 }}>
        <CardHeader title={
          <Box>
            <TextField
              id="client-name-search-input"
              placeholder='Search by Client Name'
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {loading ? <CircularProgress size={20} /> : <SearchIcon fontSize="small" />}
                  </InputAdornment>
                )
              }}
              onChange={handleTermChange}
              value={states.term}
              variant="outlined"
              sx={{ width: 280, mt: 2 }}
            />
          </Box>
        } />

        <Scrollbar>
          <Box sx={{ minWidth: 1150 }}>
            <ClientsTable 
              ActionField={
                <FunctionField label="Auto Discovery" sortable={false}
                  render={(d?: ClientManageDto & RecPatch) => {
                    const rawData = perClientSettingData.find(x => x?.clientId === d?.clientId)
                    const autoDiscoveryEnabled = rawData?.autoDiscoveryEnabled ?? false
                    return (
                      rawData === undefined ? <Skeleton variant="text" sx={{ fontSize: '1rem' }} width={100} /> :
                      <>
                        <Box>
                          <CreateOrEdit resource={securefilePerClientSettingRes} basePath="" id={rawData?.clientId ?? -1} record={rawData} {...saveHandlers} >
                            <WithSave render={({save, saving}) => (
                              <FormGroup>
                                <FormControlLabel
                                  control={
                                    <Switch checked={autoDiscoveryEnabled} name="autoDiscoveryEnabled" 
                                      disabled={saving || (states.refetchId === d?.clientId && refetchingSingle)}
                                      onChange={(e)=>{
                                        save({...rawData, autoDiscoveryEnabled: e.target.checked}, {
                                          onSuccess: () => setStates(v => ({...v, refetchId: d?.clientId ?? null}))
                                        })
                                      }} 
                                    />}
                                  label={
                                    <Typography variant="caption">
                                      {autoDiscoveryEnabled ? 'Enabled' : 'Disabled'}
                                    </Typography>}
                                />
                              </FormGroup>
                            )} />
                          </CreateOrEdit>
                        </Box>
                        <Box>
                          <FormGroup>
                            <FormControlLabel 
                              control={
                                <Tooltip title="Edit Scan Folders">
                                  <IconButton 
                                    aria-label="Edit Scan Folders"
                                    disabled={!autoDiscoveryEnabled}
                                    onClick={(e:any) => { 
                                      setStates(v => ({...v, toResume: rawData || null })) 
                                    }}
                                    sx={{ 
                                      color: 'primary.main' as any, 
                                      '&:hover': { backgroundColor: 'action.hover' },
                                    }}
                                  >
                                  <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              }
                              label={
                                <Typography variant="caption">
                                  Edit Scan Folders
                                </Typography>}
                            />
                          </FormGroup>
                        </Box>
                      </>
                    )
                  }}
                />
              }
            />
          </Box>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listCtx} />}
      </Card>
      <CreateOrEdit resource={securefilePerClientSettingRes} basePath="" id={states.toResume?.clientId ?? -1} record={states.toResume}
        {...saveHandlers}
        onSuccess={(data) => {
          saveHandlers.onSuccess(data)
          setStates(v => ({...v, toResume: null}))
          refetchClientData()
        }}
      >
        {states.toResume &&
          <EditClientScanFolders 
            open={!!states.toResume} 
            onClose={()=>{ setStates(v => ({...v, toResume: null })) }} 
          />
        }
      </CreateOrEdit>
    </>
  )
}

export default SecureFilePerClientSetting;


const defaultSort = {
  field: 'register_at',
  order: 'DESC'
}

interface EditClientScanFoldersProps {
  open: boolean;
  onClose: () => void;
}

interface AutoDiscSettings {
  scanRootFolders: string[];
  submit: any;
}

const initialVal = (record: ClientSettingDto | undefined) => ({
  settings: {
    organizationId: record?.organizationId,
    clientId: record?.clientId,
    autoDiscoveryEnabled: record?.autoDiscoveryEnabled,
    scanRootFolders: record?.scanRootFolders ?? [],
  },
  tmpRootFolderWarning: null as string|null,
  warningTimer: null as number|null,
  touched: null as {[k in keyof AutoDiscSettings]+?: boolean}|null,
  errors: null as {[k in keyof AutoDiscSettings]+?: string}|null,
})

const EditClientScanFolders: FC<EditClientScanFoldersProps> = ({
  open,
  onClose
}) => {
  const match = useMediaQuery('(max-width: 680px)')
  const width = match ? 'calc(100vw - 50px)' : 630
  const record = useRecordContext<ClientSettingDto>()
  const [states, setStates] = useState(initialVal(record))
  const validate = (s1: typeof states) => {
    const errors: any[] = []
    // if (s1.settings.autoDiscoveryEnabled && s1.settings.scanRootFolders.length === 0) {
    //   errors.push({ scanRootFolders: 'Scan Root Folders is required' })
    // }
    return errors.length === 0 ? null : errors.reduce((acc, x) => ({...acc, ...x}), {})
  }
  return (
    <Drawer
      anchor="right"
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      {record &&
        <>
        {open && 
          <Helmet>
            <title>{"Edit Scan Folders"} | OnData</title>
          </Helmet>}
          <Card>
            <CardHeader title={"Edit Scan Folders"} />
            <CardContent>
              <Grid
                container
                spacing={4}
                sx={{ minHeight: 100, maxHeight: 600, overflow: "auto" }}
              >
                <Grid item xs={12}>
                  <Alert severity="info" sx={{mt: -2 }}>
                    Please enter a list of root folders that you would like to scan for sensitive data. All subfolders within the root folders will be scanned recursively. You may use environmental variables in the root folders, such as <b>%UserProfile%</b>\Documents.
                  </Alert>
                  <StringArrayTextField list={states.settings.scanRootFolders} stackChips="column"
                    sx={{ mt: 3 }}
                    label="Scan Root Folders"
                    placeholder="Add a new Root Folder and press the Enter key to confirm..."
                    error={Boolean(states.touched?.scanRootFolders && states.errors?.scanRootFolders)}
                    helperText={states.touched?.scanRootFolders && states.errors?.scanRootFolders}
                    setList={(ls) => {
                      const filtered = filterFolderListWithoutSub(ls)
                      setStates(v => {
                        const touched = filtered !== ls || ls !== v.settings.scanRootFolders
                        let patch = null as any
                        if (filtered !== ls) { // there were subfolders been merged and reduced
                          if (v.warningTimer) { window.clearTimeout(v.warningTimer) }
                          patch = {
                            tmpRootFolderWarning: 'Subfolders have been merged.',
                            warningTimer: window.setTimeout(() => {
                              setStates(v1 => ({...v1, tmpRootFolderWarning: null, warningTimer: null}))
                            }, 5000)
                          }
                        }
                        return !touched ? v : {...v,
                          settings: {...v.settings, scanRootFolders: filtered},
                          touched: {...v.touched, scanRootFolders: true},
                          ...patch
                        }
                      })
                    }}
                    acceptValue={(s) => {
                      const errorStr = validateRootFolderString(states.settings.scanRootFolders, s)
                      if (errorStr != null) {
                        setTimeout(() => {  
                          setStates(v => {
                            if (v.warningTimer) { window.clearTimeout(v.warningTimer) }
                            return {...v,
                              tmpRootFolderWarning: errorStr,
                              warningTimer: window.setTimeout(() => {
                                setStates(v1 => ({...v1, tmpRootFolderWarning: null, warningTimer: null}))
                              }, 5000)
                            }
                          })
                        })
                      }
                      return !errorStr
                    }}
                  />
                  {states.tmpRootFolderWarning &&
                    <FormHelperText sx={{ color: 'warning.main', ml: 2 }}>
                      {states.tmpRootFolderWarning}
                    </FormHelperText>}
                </Grid>
              </Grid>
            </CardContent>
            <Stack
              direction="row"
              justifyContent="space-between" 
              sx={{ p: 2 }}
            >
              <Button
                color="primary"
                type="button"
                variant="outlined"
                onClick={onClose}
              >
                Cancel
              </Button>
              <WithSave
                render={({ save, saving}) =>
                  <Button
                    color="primary"
                    disabled={!states.touched || saving}
                    type="submit"
                    variant="contained"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      setStates(v => {
                        const v1 = {...v,
                          touched: {
                            autoDiscoveryEnabled: true,
                            scanRootFolders: true,
                          },
                        }
                        v1.errors = validate(v1)
                        if (!v1.errors) {  
                          setTimeout(() => {
                            save({...v1.settings}, {
                              onSuccess: () => {
                                setStates(v2 => ({...v2, touched: null}))
                                // onClose()
                              },
                              onFailure: (err:any) => {
                                setStates(v2 => ({...v2, errors: { submit: err.message }}))
                              },
                            })
                          })
                        }
                        return v1
                      })
                    }}
                  >
                    Save
                  </Button>
                }
              />
            </Stack>
          </Card>
        </>
      }
    </Drawer>
  )
}

const {validateRootFolderString, filterFolderListWithoutSub} = (() => { 
  const formatPattern = /^[a-zA-Z]{1}\:\\.+$/i
  const formatPattern2 = /^\%[a-zA-Z0-9_\s]+\%[\\\w\s\.]*.*$/i
  const banList = [
    "C:",
    "C:\\",
    "C:\\Users"
  ]
  const patternList = [
    /^[a-zA-Z]{1}\:\\program files(\s\(x86\))?[\\\w\s\.]*$/i,
    /^[a-zA-Z]{1}\:\\\$[\w\.]+[\\\w\s\.]*$/i,
    /^\%userprofile\%[\\]*$/i,
    /^c\:\\users\\[\w\s\.]+\\*$/i,
    /^c\:\\programdata[\\\w\s\.]*$/i,
    /^c\:\\recovery[\\\w\s\.]*$/i,
    /^c\:\\windows[\\\w\s\.]*$/i,
    /^c\:\\onedrivetemp[\\\w\s\.]*$/i,
    /^c\:\\users\\[\w\s\.]+\\appdata[\\\w\s\.]*$/i,
    /^c\:\\users\\[\w\s\.]+\\ondata[\\\w\s\.]*$/i
  ]
  const normalize = (s:string) => {
    return s.trim().replace(new RegExp('\\\\$','g'), '').toLowerCase()
  }
  const ckeckDuplication = (currentStr: string, challengeStr: string): string|undefined => {
    if (currentStr === challengeStr) {
      return 'Same value already exists.'
    }
    //do not allow to add C:\My Folder\Sub Folder if C:\My Folder already exists
    if (challengeStr.length > currentStr.length 
     && challengeStr.indexOf(currentStr + '\\') === 0
    ) {
      return 'A parent folder of the input value already exists.'
    }
  }

  return {
    validateRootFolderString: (currentList: string[], challengeStr: string) => {
      if(!formatPattern.test(challengeStr.trim()) && !formatPattern2.test(challengeStr.trim())){
        return 'This does not appear to be a valid folder.'
      }
      const nChallengeStr = normalize(challengeStr)
      for(const current of currentList){
        const nCurrent = normalize(current)
        const errorStr = ckeckDuplication(nCurrent, nChallengeStr)
        if (errorStr != null) { return errorStr }
      }
      for(const ban of banList){
        if(ban.toLowerCase() === nChallengeStr){
          return 'This value is not allowed.'
        }
      }
      for(const pattern of patternList){
        if(pattern.test(nChallengeStr)){
          return 'This value is not allowed.'
        }
      }
    },
    filterFolderListWithoutSub: (currentList: string[]) => {
      const pairs = currentList.map((s, i) => [normalize(s), i] as const).sort((a, b) => a[0].length <= b[0].length ? -1 : 1)
      const subfolders = new Set<number>()
      for(let j = 0; j < pairs.length - 1; j++) {
        const [s, i] = pairs[j]
        if (subfolders.has(i)) { continue; }
        const s_ = s + '\\'
        for(let n = j + 1; n < pairs.length; n++) {
          const other = pairs[n]
          if (subfolders.has(other[1])) { continue; }
          if (other[0].indexOf(s_) === 0) {
            subfolders.add(other[1])
          }
        }
      }
      return subfolders.size === 0 ? currentList :
        currentList.filter((s, i) => !subfolders.has(i))
    }
  }
})();
